<template>
  <SectionFrame v-if="posts.length || inStudio" :section="section" :index="index" :heading="heading" label="Latest posts">
    <template v-if="posts.length">
      <div v-if="layout === 'lead-grid'" class="s2-posts-lead">
        <a :href="back(`/post/${posts[0].slug}`)" class="t-link s2-post-lead mo-reveal">
          <img v-if="posts[0].cover" :src="posts[0].cover" v-bind="imgAttrs(posts[0].cover, '(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw')" alt="" class="t-photo s2-post-img" loading="lazy">
          <div><p v-if="section.showDates !== false && posts[0].date" class="s2-muted s2-small">{{ dateText(posts[0]) }}</p>
            <h3 class="t-h2 s2-post-title">{{ posts[0].title }}</h3>
            <p v-if="section.showExcerpt !== false && posts[0].excerpt" class="t-body s2-muted">{{ posts[0].excerpt }}</p></div>
        </a>
        <ul class="s2-posts-side mo-stagger">
          <li v-for="p in posts.slice(1)" :key="p.id"><a :href="back(`/post/${p.slug}`)" class="t-link s2-post-row">
            <img v-if="p.cover" :src="p.cover" v-bind="imgAttrs(p.cover, '(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw')" alt="" class="t-photo s2-post-thumb" loading="lazy">
            <span><span v-if="section.showDates !== false && p.date" class="s2-muted s2-small">{{ dateText(p) }}</span><b class="s2-post-name">{{ p.title }}</b></span></a></li>
        </ul>
      </div>
      <ul v-else-if="layout === 'list'" class="s2-posts-list mo-stagger">
        <li v-for="p in posts" :key="p.id"><a :href="back(`/post/${p.slug}`)" class="t-link s2-post-line">
          <b>{{ p.title }}</b><span v-if="section.showDates !== false && p.date" class="s2-muted s2-small">{{ dateText(p) }}</span></a></li>
      </ul>
      <div v-else-if="layout === 'river'" class="s2-posts-river mo-stagger">
        <a v-for="p in posts" :key="p.id" :href="back(`/post/${p.slug}`)" class="t-link s2-post-river">
          <img v-if="p.cover" :src="p.cover" v-bind="imgAttrs(p.cover, '(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw')" alt="" class="t-photo s2-post-img" loading="lazy">
          <div><p v-if="section.showDates !== false && p.date" class="s2-muted s2-small">{{ dateText(p) }}</p>
            <h3 class="t-h3 s2-post-title">{{ p.title }}</h3>
            <p v-if="section.showExcerpt !== false && p.excerpt" class="t-body s2-muted">{{ p.excerpt }}</p></div>
        </a>
      </div>
      <div v-else class="s2-grid mo-stagger" style="--s2-cols: 3">
        <a v-for="p in posts" :key="p.id" :href="back(`/post/${p.slug}`)" class="t-card t-link s2-post-card">
          <img v-if="p.cover" :src="p.cover" v-bind="imgAttrs(p.cover, '(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw')" alt="" class="t-photo s2-post-img" loading="lazy">
          <p v-if="section.showDates !== false && p.date" class="s2-muted s2-small">{{ dateText(p) }}</p>
          <h3 class="t-h3 s2-post-title">{{ p.title }}</h3>
          <p v-if="section.showExcerpt !== false && p.excerpt" class="t-body s2-muted s2-clamp">{{ p.excerpt }}</p>
        </a>
      </div>
      <p class="s2-more"><a :href="back('/posts')" class="t-link s2-u">{{ section.moreText || "All posts" }}</a></p>
    </template>
    <StudioGap v-else>Write your first post, then it shows here.</StudioGap>
  </SectionFrame>
</template>

<script setup>
import {imgAttrs} from "@/utils/photoSizes.js";
import {computed} from "vue";
import SectionFrame from "./SectionFrame.vue";
import StudioGap from "./StudioGap.vue";
import {layoutOf, niceDate, pickPosts, readingText} from "@/utils/sectionLogic.js";
import {studioMode} from "@/classes/previewBridge.js";
import {useBackLinks} from "@/composables/useBackLinks.js";
const back = useBackLinks();

const props = defineProps({section: {type: Object, required: true}, index: {type: Number, default: 0}, data: {type: Object, default: () => ({})}});
const inStudio = computed(() => studioMode.value !== "off");
const layout = computed(() => layoutOf(props.section, ["lead-grid", "river", "list", "cards"]));
const heading = computed(() => props.section.heading ?? "Latest news");
const posts = computed(() => pickPosts(props.data.posts, props.section));
const dateText = (p) => [niceDate(p.date).replace(/^\w+ /, ""), readingText(p.readingMinutes)].filter(Boolean).join(" · ");
</script>

<style>
.s2-posts-lead { display: grid; grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr); gap: var(--t-gap); align-items: start; }
@media (max-width: 860px) { .s2-posts-lead { grid-template-columns: minmax(0, 1fr); } }
.s2-post-img { width: 100%; height: auto; aspect-ratio: 3 / 2; object-fit: cover; display: block; margin-bottom: .75rem; }
.s2-post-title { margin: .25rem 0 .4rem; }
.s2-posts-side { list-style: none; padding: 0; margin: 0; display: grid; gap: 1rem; }
.s2-post-row { display: grid; grid-template-columns: 96px 1fr; gap: .9rem; align-items: center; }
.s2-post-row > span { display: flex; flex-direction: column; }
.s2-post-thumb { width: 96px; height: auto; aspect-ratio: 1; object-fit: cover; }
.s2-post-name { line-height: 1.3; }
.s2-posts-list { list-style: none; padding: 0; margin: 0; max-width: 760px; }
.s2-post-line { display: flex; justify-content: space-between; gap: 1rem; padding: .85rem 0; border-bottom: 1px solid var(--t-line); }
.s2-posts-river { display: grid; gap: 1.75rem; max-width: 860px; }
.s2-post-river { display: grid; grid-template-columns: 240px 1fr; gap: 1.25rem; align-items: start; }
.s2-post-river .s2-post-img { margin: 0; }
@media (max-width: 640px) { .s2-post-river { grid-template-columns: 1fr; } }
.s2-post-card { display: flex; flex-direction: column; }
.s2-clamp { display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }
.s2-more { margin-top: 1.5rem; }
.s2-post-card:hover .s2-post-title, .s2-post-river:hover .s2-post-title, .s2-post-lead:hover .s2-post-title { text-decoration: underline; }
</style>
